import {type FilmFrameReader} from '#/lib/flashbookFilmFrames'

/**
 * The browser's own film-frame reader.
 *
 * A seek and a canvas. The element is **detached** — never appended to the
 * document — because a strip is a picture and must not be able to make a sound:
 * a stray film element in the page is exactly how a creator ends up hearing a
 * preview they cannot see.
 *
 * Frames are cached per film, keyed by the times they were taken for. Opening the
 * trimmer again on the same film (to change the minute, which is the common
 * second visit) then costs no decoding at all.
 */

type Entry = {
  /** The element the frames are read through, kept for reuse. */
  video: HTMLVideoElement
  /** Only frames that produced pixels; a gap is a frame that failed. */
  frames: (string | undefined)[]
}

const cache = new Map<string, Entry>()

/**
 * Elements kept for reading a *still* — one per film, separate from the strip's.
 *
 * Separate because they are configured differently, and the difference is the
 * point: a strip's element pulls the film down so eight seeks can be served from
 * it, while a still's asks only for what a single frame needs. A feed holding
 * twenty book covers must not start twenty downloads.
 */
const posterCache = new Map<string, HTMLVideoElement>()

/**
 * A canvas the size of the picture being read, scaled down from the film's own
 * proportions by one factor on both axes.
 */
function canvasFor(
  video: HTMLVideoElement,
  targetWidth: number,
): {canvas: HTMLCanvasElement; context: CanvasRenderingContext2D | null} {
  const canvas = document.createElement('canvas')
  const width = video.videoWidth || targetWidth
  const height = video.videoHeight || Math.round((width * 9) / 16)
  const scale = Math.min(1, targetWidth / width)
  canvas.width = Math.max(1, Math.round(width * scale))
  canvas.height = Math.max(1, Math.round(height * scale))
  return {canvas, context: canvas.getContext('2d')}
}

/** Seek the element to an instant and wait for the picture to be there. */
async function seekTo(video: HTMLVideoElement, atMs: number): Promise<void> {
  const durationMs = Number.isFinite(video.duration) ? video.duration * 1000 : 0
  const target = Math.min(
    Math.max(0, atMs),
    Math.max(0, durationMs - END_EPSILON_MS),
  )
  if (Math.abs(video.currentTime - target / 1000) <= 0.01) return
  video.currentTime = target / 1000
  await waitFor(video, 'seeked', 4000)
}

/** Longest edge of a decoded frame — a strip cell is ~40px wide. */
const FRAME_WIDTH = 160

/** Seek this close to the end of the film and the browser has no frame to give. */
const END_EPSILON_MS = 60

function waitFor(
  target: HTMLVideoElement,
  event: string,
  timeoutMs: number,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      target.removeEventListener(event, done)
      reject(new Error(`timed out waiting for ${event}`))
    }, timeoutMs)
    function done() {
      clearTimeout(timer)
      target.removeEventListener(event, done)
      resolve()
    }
    target.addEventListener(event, done)
  })
}

/**
 * A film element configured to be read from.
 *
 * `crossOrigin` is only asked for on a remote film. An uploaded cover lives on
 * the PDS, and without the attribute the canvas is *tainted* the moment a frame is
 * drawn and `toDataURL` throws — so the frames would be readable and then
 * unusable, which is the worst of both. Asking for CORS instead means the browser
 * either allows the read or refuses the load, and a refused load is a gap in the
 * strip rather than an exception in the middle of drawing one.
 *
 * `preload` is the one thing the two readers disagree about, and deliberately: a
 * strip is about to ask for eight instants spread across the whole film and so
 * pulls it down, while a single still wants the frame and nothing else and leaves
 * the browser to fetch the range it needs.
 */
function createVideo(
  uri: string,
  preload: 'auto' | 'metadata' = 'auto',
): HTMLVideoElement {
  const video = document.createElement('video')
  video.preload = preload
  video.muted = true
  video.playsInline = true
  video.crossOrigin = 'anonymous'
  video.src = uri
  return video
}

/** Silence an element and let go of its bytes. */
function stop(video: HTMLVideoElement): void {
  try {
    video.pause()
    video.removeAttribute('src')
    video.load()
  } catch {}
}

/**
 * The film's own proportions, scaled down together.
 *
 * Both axes take the *same* factor, which is the whole point: a cell shows a
 * picture of a frame, and a frame drawn to the wrong aspect is not a smaller
 * version of the film, it is a different picture. This used to multiply the
 * height by an extra 9/16, so every frame came out at about 3:1 and the strip's
 * `cover` crop then showed a narrow vertical slice of the middle of it — a
 * strip of the film's smear rather than of its frames.
 *
 * The `|| ` fallbacks are for an element that reported no dimensions at all:
 * 16:9 is the shape a cover film is shot in, so a guess still lays the strip out.
 */
async function readInto(
  video: HTMLVideoElement,
  timesMs: number[],
): Promise<(string | undefined)[]> {
  const {canvas, context} = canvasFor(video, FRAME_WIDTH)
  if (!context) return timesMs.map(() => undefined)

  const durationMs = Number.isFinite(video.duration) ? video.duration * 1000 : 0
  if (durationMs <= 0) return timesMs.map(() => undefined)

  const out: (string | undefined)[] = []
  for (const at of timesMs) {
    try {
      await seekTo(video, at)
      context.drawImage(video, 0, 0, canvas.width, canvas.height)
      out.push(canvas.toDataURL('image/jpeg', 0.6))
    } catch {
      // A frame the browser will not seek to or will not let us draw is a gap in
      // the strip: the film around it is still perfectly trimmable.
      out.push(undefined)
    }
  }
  return out
}

export function createPlatformFilmFrameReader(): FilmFrameReader {
  return {
    read: async (uri, timesMs) => {
      if (typeof document === 'undefined') return timesMs.map(() => undefined)

      let entry = cache.get(uri)
      if (!entry) {
        const video = createVideo(uri)
        entry = {video, frames: []}
        cache.set(uri, entry)
      }
      const {video} = entry

      try {
        if (video.readyState < 1) {
          video.load()
          await waitFor(video, 'loadeddata', 8000)
        }
      } catch {
        return timesMs.map(() => undefined)
      }

      /* Retried on every ask, including a partially-failed one: a film that was
       * still downloading when the strip first drew will have frames the second
       * time, and a cache that remembered \"no\" forever would strand the strip
       * empty for the whole visit. */
      return readInto(video, timesMs)
    },

    readPoster: async (uri, atMs, widthPx) => {
      if (typeof document === 'undefined') return undefined

      let held = posterCache.get(uri)
      if (!held) {
        held = createVideo(uri, 'metadata')
        posterCache.set(uri, held)
      }
      const video = held

      try {
        if (video.readyState < 1) {
          video.load()
          await waitFor(video, 'loadeddata', 8000)
        }
        const {canvas, context} = canvasFor(video, widthPx)
        if (!context) return undefined
        await seekTo(video, atMs)
        context.drawImage(video, 0, 0, canvas.width, canvas.height)
        return canvas.toDataURL('image/jpeg', 0.78)
      } catch {
        /*
         * A still the browser will not draw — a film it cannot decode, or one
         * served without the headers a canvas needs — leaves the caller with what
         * it had. The element is dropped rather than kept, so the next card does
         * not inherit a failed read.
         */
        posterCache.delete(uri)
        try {
          video.pause()
          video.removeAttribute('src')
          video.load()
        } catch {}
        return undefined
      }
    },

    release: uri => {
      const entry = cache.get(uri)
      if (entry) {
        cache.delete(uri)
        stop(entry.video)
      }
      const poster = posterCache.get(uri)
      if (poster) {
        posterCache.delete(uri)
        stop(poster)
      }
    },
  }
}
